import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener } from '@angular/core';
import { RoleType } from 'app/shared/services/user/user-application-properties.model';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { UserCreationValidators } from 'app/validators/user-create/user-creation-validators';
import { Application } from 'app/shared/services/app/application/application.model';
import { Session } from 'app/shared/services/session/session.service';
import { ApplicationService } from 'app/shared/services/app/application/application.service';
import { LoaderButton } from 'app/shared/components/loader-button/loader-button.component';

@Component({
  selector: 'tog-app-users-builder',
  templateUrl: './app-users-builder.component.html',
  styleUrls: ['./app-users-builder.component.scss']
})
export class AppUsersBuilderComponent implements OnInit, OnDestroy {
  RoleType = RoleType;
  app: Application;
  addUserForm: FormGroup;
  appFeatures: any[] = []; // will hold features actually present on app

  @Input() userToEdit: any;
  @Input() defaultRole = RoleType.User;
  newUserRole: number = this.defaultRole;

  // state variables
  userEdited: boolean = false;
  emailInvalid: boolean = false;
  resetRole: boolean = false;
  activeSection: string = 'email';

  emailSubscription: any;

  @Input() maxUsersReached: boolean = false;
  @Input() alreadyInApp: boolean = false;
  @Input() alreadyInUserList: boolean = false;

  @Output('userAdded') userAdded: EventEmitter<any> = new EventEmitter<any>();
  @Output('userUpdated') userUpdated: EventEmitter<any> = new EventEmitter<any>();
  @Output('cancelEdit') cancelEdit: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('cancelEditUsersButton') cancelEditUsersButton: ElementRef;

  @ViewChild('confirmSaveUserButton') confirmSaveUserButton: LoaderButton;

  constructor(private fb: FormBuilder,
              private _session: Session,
              private _appService: ApplicationService) {
    this.addUserForm = fb.group({
      email: ['', [Validators.required, UserCreationValidators.mustBeEmail]]
    });
  }

  ngOnInit() {
    this._session.user().then(() => {
      return this._appService.getApplicationDetails(this._session.selectedAppSysName);
    })
    .then((app: Application) => {
      this.app = app;
      this.appFeatures = this._appService.getAppFeatures(true);
      this.appFeatures.forEach((feature) => {
        if(feature.display_name === 'Home Screen Pods') { feature.display_name = 'Pods'; feature.icon = 'oi oi-grid-three-up'; }
        if(feature.display_name === 'Push Notifications') { feature.display_name = 'Push'; }
        if(feature.display_name === 'App Content') { feature.display_name = 'Content'; }
      });
      // selects appropriate features if we're editing an existing user
      if (this.userToEdit) {
        this.addUserForm.controls['email'].setValue(this.userToEdit.email);
        this.defaultRole = this.userToEdit.role;
        this.selectFeaturesForEditing();
        this.userEdited = false;
      }
    });

    // if user is typing, clear error messages
    this.emailSubscription = this.addUserForm.controls['email'].valueChanges.subscribe((value) => {
      if (value != '') {
        this.resetErrors();
        this.resetRole = false;
      }
      if (this.userToEdit) {
        this.userEdited = true;
      }
    });
  }

  resetErrors() {
    this.emailInvalid = false;
    this.alreadyInUserList = false;
    this.alreadyInApp = false;
    this.maxUsersReached = false;
  }

  checkEmail() {
    this.emailInvalid = !this.addUserForm.valid;
    if (this.addUserForm.controls['email'].value == '') { this. emailInvalid = false; }
  }

  // this is two different for loop styles so we can break out and save some time
  selectFeaturesForEditing() {
    if (this.userToEdit.role == RoleType.Manager) {
      this.appFeatures.forEach((feature) => {
        for (let i = 0; i < this.userToEdit.features.length; i++) {
          if (feature.system_name === this.userToEdit.features[i].system_name) {
            feature.selected = this.userToEdit.features[i].selected;
            return;
          }
        }
      });
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload($event) {
    if (this.addUserForm.dirty) {
      $event.returnValue = true;
      return true;
    }
  }

  featureToggled() {
    if (this.userToEdit) {
      this.userEdited = true;
    }
  }

  onRoleSelect(type: number) {
    if (this.userToEdit && type != this.newUserRole) {
      this.userEdited = true;
    }
    this.newUserRole = type;
  }

  saveUser() {
    let newUserEmail = this.addUserForm.controls['email'].value;
    if (this.checkIfEmailValid(newUserEmail)) {
      let newUser: any = {
        email: newUserEmail,
        role: this.newUserRole || this.defaultRole
      };

      // since only Managers should have specified features
      if (newUser.role == RoleType.Manager) {
        newUser.features = [];
        for (var i = 0; i < this.appFeatures.length; i++) {
          newUser.features.push({
            display_name: this.appFeatures[i].display_name,
            system_name: this.appFeatures[i].system_name,
            icon: this.appFeatures[i].icon,
            selected: this.appFeatures[i].selected
          });
        }
      }

      if (this.userToEdit) {
        this.userToEdit = null;
        this.userUpdated.emit(newUser);
      } else {
        this.userAdded.emit(newUser);
      }
      this.confirmSaveUserButton.setConfirmed();
    } else {
      this.emailInvalid = true;
      this.confirmSaveUserButton.setInactive();
    }
  }

  resetForm() {
    this.addUserForm.controls['email'].setValue('');
    this.defaultRole = RoleType.User;
    this.resetRole = true;
    this.appFeatures.forEach((feature) => {
      feature.selected = false;
    });
  }

  checkIfEmailValid(email: string): boolean {
    let patternUrl = /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i
    return patternUrl.test(email);
  }

  isUserFormValid() {
    let featuresAreValid = true; // defaults to true because it should only trip for Managers
    // if the new user is a Manager it should have at least one feature selected
    if (this.newUserRole == RoleType.Manager) {
      featuresAreValid = this.appFeatures.filter(feature => feature.selected).length > 0;
    }
    return this.addUserForm.controls['email'].value == '' ||
           !this.checkIfEmailValid(this.addUserForm.controls['email'].value) ||
           (!this.newUserRole && !this.userToEdit) ||
           !featuresAreValid;
  }

  cancelButton() {
    if (this.userEdited) {
      this.cancelEditUsersButton.nativeElement.click();
    } else {
      this.confirmCancel();
    }
  }

  confirmCancel() {
    this.userEdited = false;
    this.userToEdit = null;
    this.cancelEdit.emit();
  }

  ngOnDestroy() {
    if (this.emailSubscription) {
      this.emailSubscription.unsubscribe();
    }
  }

}



// WEBPACK FOOTER //
// /app/src/app/shared/components/app-users-builder/app-users-builder.component.ts